'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { VehicleGroupForm, type VehicleGroupFormRef } from '@/components/partials/vehicle-groups'
import { ContentError, FormSkeleton } from '@/components/partials'
import { type CreateVehicleGroupParams } from '@/types/vehicle-group'
import { useVehicleGroup, useUpdateVehicleGroup } from '@/hooks'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const vehicleGroupFormRef = useRef<VehicleGroupFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: vehicleGroup, isLoading, error } = useVehicleGroup(id)
  const { mutate: updateVehicleGroup, isPending } = useUpdateVehicleGroup()

  const handleSubmit = (data: CreateVehicleGroupParams) => {
    updateVehicleGroup(
      { id, params: data },
      {
        onSuccess: async updatedVehicleGroup => {
          setIsRedirecting(true)
          queryClient.setQueryData(['vehicle-group', id], updatedVehicleGroup)
          await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })
          toast.success(t('updated_successfully'))
          router.push(`/products/vehicle-groups/${updatedVehicleGroup.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (vehicleGroupFormRef.current) {
        vehicleGroupFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/products/vehicle-groups/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={1} showPictures />
  }

  if (error || !vehicleGroup) {
    return <ContentError error={error} listUrl="/products/vehicle-groups" />
  }

  return (
    <VehicleGroupForm
      ref={vehicleGroupFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={vehicleGroup}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
